var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
export var EquipmentType;
(function (EquipmentType) {
    EquipmentType["QsStandalone"] = "-MDAF_dguZcRLLwbV5o5";
    EquipmentType["Quantum"] = "-NLS7Zv-F12TV-X4-MPk";
    EquipmentType["RadioRa2"] = "-MHHnSsoIPgJk2H4mr6l";
})(EquipmentType || (EquipmentType = {}));
const actionsMap = {
    lutronAreaRaiseLower: {
        raiseLowerStop: [2, 3, 4],
    },
    lutronSceneControllerRaiseLower: {
        raiseLowerStop: [18, 19, 20],
    },
    lutronOutputRaiseLower: {
        raiseLowerStop: [2, 3, 4],
        tiltRaiseLowerStop: [11, 12, 13],
        liftRaiseLowerStop: [14, 15, 16],
    },
    lutronShadeGroupRaiseLower: {
        raiseLowerStop: [2, 3, 4],
        tiltRaiseLowerStop: [16, 17, 18],
        liftRaiseLowerStop: [19, 20, 21],
    },
};
function isLutronLightingControl(config) {
    return 'integrationId' in config && 'zoneNumber' in config;
}
function isLutronPhantomButton(config) {
    return config.type === 'phantomButton' && 'buttonNumber' in config && 'integrationId' in config;
}
function isLutronScene(config) {
    return config.type === 'scene' && 'sceneNumber' in config && 'integrationId' in config;
}
export function isLutronShadeControlConfig(config) {
    return (config.type === 'lutronAreaRaiseLower' ||
        config.type === 'lutronButtonPress' ||
        config.type === 'lutronSceneControllerRaiseLower' ||
        config.type === 'lutronOutputRaiseLower' ||
        config.type === 'lutronShadeGroupRaiseLower');
}
export function isLutronShadePresetConfig(config) {
    return (config.type === 'lutronAreaScene' ||
        config.type === 'lutronButtonPreset' ||
        config.type === 'lutronButtonPress' ||
        config.type === 'lutronSceneControllerScene' ||
        config.type === 'lutronShadeGroupPreset');
}
// Note: all regular expressions have the "i" flag because sometimes the NWK responds with the
// integration id in all caps.
export class Lutron {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.framework = framework;
        // other class properties and constructor
        this.equipmentType = EquipmentType.QsStandalone;
        this.holdTimeoutMs = 1000;
        this.lightingPresets = {};
        this.lightingZones = {};
        this.shadeControlConfigs = {};
        this.shadeControlTimeouts = {};
        this.shadePresetConfigs = {};
        this.timeouts = new Timeouts();
        this.qsStandaloneInitializerSteps = [
            {
                errorCode: 'verifyFailed',
                send: 'aveo',
                succeed: /~Error,\d|connection established/,
                next: /login incorrect|failed login/,
            },
            {
                errorCode: 'authFailed',
                send: (cred) => { var _a; return (_a = cred.password) !== null && _a !== void 0 ? _a : ''; },
                succeed: /connection established/,
                fail: /login incorrect/,
            },
        ];
        this.qsStandalonePassphraseInitializerSteps = [
            {
                errorCode: 'authFailed',
                send: (cred) => {
                    var _a;
                    return (_a = cred.username) !== null && _a !== void 0 ? _a : '';
                },
                unterminatedRegExp: /password:|passphrase:|connection established/i,
                next: /password:|passphrase:/i,
                fail: /failed login/i,
                succeed: /connection established/i,
            },
            {
                errorCode: 'authFailed',
                send: (cred) => {
                    var _a;
                    return (_a = cred.password) !== null && _a !== void 0 ? _a : '';
                },
                succeed: /connection established/i,
                fail: /failed login/i,
            },
        ];
        this.quantumInitializerSteps = [
            {
                errorCode: 'verifyFailed',
                unterminatedRegExp: /login:/i,
                next: /login:/i,
                succeed: /connection established/i,
            },
            {
                errorCode: 'authFailed',
                send: (cred) => {
                    var _a;
                    return (_a = cred.username) !== null && _a !== void 0 ? _a : '';
                },
                unterminatedRegExp: /password:|connection established/i,
                next: /password:/i,
                fail: /failed login/i,
                succeed: /connection established/i,
            },
            {
                errorCode: 'authFailed',
                send: (cred) => {
                    var _a;
                    return (_a = cred.password) !== null && _a !== void 0 ? _a : '';
                },
                succeed: /connection established/i,
                fail: /failed login/i,
            },
        ];
        this.radioRa2TelnetInitializerSteps = [
            {
                errorCode: 'verifyFailed',
                next: /login:/,
                unterminatedRegExp: /.+:/,
            },
            {
                errorCode: 'verifyFailed',
                send: (cred) => { var _a; return (_a = cred.username) !== null && _a !== void 0 ? _a : ''; },
                next: /password:/,
                unterminatedRegExp: /.+:/,
            },
            {
                errorCode: 'authFailed',
                send: (cred) => { var _a; return (_a = cred.password) !== null && _a !== void 0 ? _a : ''; },
                fail: /bad login/,
                succeed: /GNET>/,
                unterminatedRegExp: /.+>/,
            },
        ];
        this.radioRa2SerialInitializerSteps = [
            {
                errorCode: 'verifyFailed',
                send: '?SYSTEM,1',
                succeed: /~SYSTEM,1,.+/,
            },
        ];
        // controls
        this.lightingLevel = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                const zone = this.lightingZones[key];
                if (zone === undefined)
                    throw new Error(`unexpected lighting control key: ${key}`);
                let match;
                if (this.equipmentType === EquipmentType.QsStandalone) {
                    match = yield this.socket.send(`?DEVICE,${zone.integrationId},${zone.zoneNumber},14`, new RegExp(`~DEVICE,${zone.integrationId},${zone.zoneNumber},14,(.+)|~Error`, 'i'), tags);
                }
                else {
                    match = yield this.socket.send(`?OUTPUT,${zone.integrationId},1`, new RegExp(`~OUTPUT,${zone.integrationId},1,(.+)|~Error`, 'i'), tags);
                }
                if (match[1] === undefined)
                    return (_a = this.state.lightingLevel.values[key]) !== null && _a !== void 0 ? _a : 0;
                zone.level = parseFloat(match[1]);
                return zone.level;
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _b;
                const zone = this.lightingZones[key];
                if (zone === undefined)
                    throw new Error(`unexpected lighting control key: ${key}`);
                let match;
                if (this.equipmentType === EquipmentType.QsStandalone) {
                    match = yield this.socket.send(`#DEVICE,${zone.integrationId},${zone.zoneNumber},14,${value}`, new RegExp(`~DEVICE,${zone.integrationId},${zone.zoneNumber},14,(.+)|~Error`, 'i'), tags);
                }
                else {
                    match = yield this.socket.send(`#OUTPUT,${zone.integrationId},1,${value}`, new RegExp(`~OUTPUT,${zone.integrationId},1,(.+)|~Error`, 'i'), tags);
                }
                if (match[1] === undefined)
                    return (_b = this.state.lightingLevel.values[key]) !== null && _b !== void 0 ? _b : 0;
                zone.level = parseFloat(match[1]);
                this.state.lightingPower.update(key, zone.level !== 0);
                return zone.level;
            }),
            range: { max: 100, min: 0, round: 1 },
            getKey: (config) => {
                if (!isLutronLightingControl(config)) {
                    throw new Error(`invalid lighting config: ${JSON.stringify(config, null, 4)}`);
                }
                const key = `${config.integrationId}.${config.zoneNumber}`;
                this.lightingZones[key] = {
                    integrationId: config.integrationId,
                    level: 0,
                    zoneNumber: config.zoneNumber,
                };
                return key;
            },
            validate: (config) => __awaiter(this, void 0, void 0, function* () {
                return { integrationId: 'ok', zoneNumber: 'ok' };
            }),
            asyncHandlers: {
                regExp: /~DEVICE,([^,]+),([^,]+),14,(.+)|~OUTPUT,([^,]+),1,(.+)/i,
                callback: (match) => {
                    if (this.framework.phase !== 'controlling')
                        return;
                    const integrationId = match[1] !== undefined ? match[1] : match[4];
                    const zoneKey = match[2] !== undefined ? match[2] : '1';
                    const valueStr = match[3] !== undefined ? match[3] : match[5];
                    const key = `${integrationId}.${zoneKey}`;
                    const zone = this.lightingZones[key];
                    if (zone === undefined)
                        return;
                    zone.level = parseFloat(valueStr);
                    this.state.lightingLevel.update(key, zone.level);
                    this.state.lightingPower.update(key, zone.level !== 0);
                },
            },
        };
        this.lightingPower = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                // Assume that lightingLevel was queried before lightingPower.
                const zone = this.lightingZones[key];
                if (zone === undefined)
                    throw new Error(`unexpected lighting control key: ${key}`);
                return zone.level !== 0;
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _c, _d;
                const zone = this.lightingZones[key];
                if (zone === undefined)
                    throw new Error(`unexpected lighting control key: ${key}`);
                let newLevel = value ? (_c = this.state.lightingLevel.values[key]) !== null && _c !== void 0 ? _c : 0 : 0;
                if (value && newLevel === 0)
                    newLevel = 100;
                let match;
                if (this.equipmentType === EquipmentType.QsStandalone) {
                    match = yield this.socket.send(`#DEVICE,${zone.integrationId},${zone.zoneNumber},14,${newLevel}`, new RegExp(`~DEVICE,${zone.integrationId},${zone.zoneNumber},14,(.+)|~Error`, 'i'), [`set.lightingLevel.${key}`]);
                }
                else {
                    match = yield this.socket.send(`#OUTPUT,${zone.integrationId},${zone.zoneNumber},${newLevel}`, new RegExp(`~OUTPUT,${zone.integrationId},${zone.zoneNumber},(.+)|~Error`, 'i'), [`set.lightingLevel.${key}`]);
                }
                if (match[1] === undefined)
                    return (_d = this.state.lightingPower.values[key]) !== null && _d !== void 0 ? _d : false;
                zone.level = parseFloat(match[1]);
                if (value)
                    this.state.lightingLevel.update(key, zone.level);
                return zone.level !== 0;
            }),
            getKey: (config) => {
                if (!isLutronLightingControl(config)) {
                    throw new Error(`invalid lighting config: ${JSON.stringify(config, null, 4)}`);
                }
                const key = `${config.integrationId}.${config.zoneNumber}`;
                this.lightingZones[key] = {
                    integrationId: config.integrationId,
                    level: 0,
                    zoneNumber: config.zoneNumber,
                };
                return key;
            },
        };
        this.lightingPreset = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const preset = this.lightingPresets[key];
                if (preset === undefined)
                    throw new Error(`unexpected lighting preset key: ${key}`);
                if (isLutronScene(preset)) {
                    if (this.equipmentType === EquipmentType.Quantum) {
                        // The Quantum doesn't support querying DEVICE scenes. In the future we may add
                        // support for AREA scenes which can be queried. If we do add AREA support, it
                        // would probably be a new preset type such as 'areaScene'.
                        return false;
                    }
                    else {
                        const match = yield this.socket.send(`?DEVICE,${preset.integrationId},141,7`, new RegExp(`~DEVICE,${preset.integrationId},141,7,(\\d+)`, 'i'));
                        const sceneNumber = parseInt(match[1], 10);
                        return preset.sceneNumber === sceneNumber;
                    }
                }
                // We can't query the LED state for the scene buttons on the QS Standalone or a virtual
                // LED for the phantom buttons, so just always report that they are not active.
                return false;
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const preset = this.lightingPresets[key];
                if (preset === undefined)
                    throw new Error(`unexpected lighting preset key: ${key}`);
                if (preset.type === 'phantomButton') {
                    yield this.socket.send(`#DEVICE,${preset.integrationId},${preset.buttonNumber},3`, new RegExp(`~DEVICE,${preset.integrationId},${preset.buttonNumber},3|~Error`, 'i'), tags);
                }
                else if (preset.type === 'scene') {
                    // We use sendAsync because the Lutron doesn't respond to the scene command if it
                    // is already in that scene. If we do get a response, it will be handled by this
                    // control's async hander.
                    yield this.socket.sendAsync(`#DEVICE,${preset.integrationId},141,7,${preset.sceneNumber}`, tags);
                }
                // scene button implementation in case we need it later
                // } else if (preset.type === 'sceneButton') {
                //     // map button numbers (0-4) to component numbers; only valid for Grafik Eye
                //     const componentNumbers: Array<string> = ['83', '70', '71', '76', '77'];
                //     const componentNumber = componentNumbers[preset.buttonNumber];
                //     if (componentNumber === undefined) {
                //         throw new Error(`invalid button number: ${preset.buttonNumber}`);
                //     }
                //     await this.socket.send(
                //         `#DEVICE,${preset.integrationId},${componentNumber},3`,
                //         new RegExp(`~DEVICE,${preset.integrationId},${componentNumber},3`, 'i'),
                //         tags
                //     );
                // }
                // Turn off the preset active flag for other scenes on the same device since only one
                // can be active at a time.
                for (const k of Object.keys(this.lightingPresets)) {
                    if (k === key)
                        continue;
                    if (this.lightingPresets[k].integrationId === preset.integrationId) {
                        this.state.lightingPreset.update(k, false);
                    }
                }
                // We can only track the active state for scenes, not phantom buttons or scene buttons.
                // (Note that Quantum can't query scenes.)
                return preset.type === 'scene' && this.equipmentType !== EquipmentType.Quantum;
            }),
            getKey: (config) => {
                let key;
                if (isLutronPhantomButton(config)) {
                    key = `${config.integrationId}.phantom.${config.buttonNumber}`;
                }
                else if (isLutronScene(config)) {
                    key = `${config.integrationId}.scene.${config.sceneNumber}`;
                }
                else {
                    throw new Error(`invalid lighting preset config: ${JSON.stringify(config, null, 4)}`);
                }
                this.lightingPresets[key] = Object.assign({}, config);
                return key;
            },
            validate: (config) => __awaiter(this, void 0, void 0, function* () {
                if (config.type === 'phantomButton') {
                    return { integrationId: 'ok', buttonNumber: 'ok' };
                }
                else {
                    return { integrationId: 'ok', sceneNumber: 'ok' };
                }
            }),
            asyncHandlers: {
                regExp: /~DEVICE,([^,]+),141,7,(-?\d+)$/i,
                callback: (match) => {
                    if (this.framework.phase !== 'controlling')
                        return;
                    const integrationId = match[1];
                    const sceneNumber = parseInt(match[2], 10);
                    // The Grafik Eye thinks the scene number is -1 if you set the same scene twice.
                    if (sceneNumber === -1)
                        return;
                    for (const key of Object.keys(this.lightingPresets)) {
                        const preset = this.lightingPresets[key];
                        if (preset.type === 'scene' && preset.integrationId === integrationId) {
                            this.state.lightingPreset.update(key, preset.sceneNumber === sceneNumber);
                        }
                    }
                },
            },
        };
        this.shadeOperation = {
            set: (key, op, tags) => __awaiter(this, void 0, void 0, function* () {
                var _e, _f, _g, _h, _j, _k, _l;
                const config = this.shadeControlConfigs[key];
                if (config === undefined) {
                    this.log.error(`missing shade control config for key = ${key}`);
                }
                else if (config.type === 'lutronButtonPress') {
                    const buttonNumber = op.type === 'position'
                        ? (_g = (_f = (_e = config.position) === null || _e === void 0 ? void 0 : _e.buttons) === null || _f === void 0 ? void 0 : _f[op.motion]) === null || _g === void 0 ? void 0 : _g.buttonNumber
                        : (_k = (_j = (_h = config.tilt) === null || _h === void 0 ? void 0 : _h.buttons) === null || _j === void 0 ? void 0 : _j[op.motion]) === null || _k === void 0 ? void 0 : _k.buttonNumber;
                    if (buttonNumber === undefined)
                        return op;
                    let timeout = this.shadeControlTimeouts[key];
                    if (timeout === undefined) {
                        timeout = this.timeouts.create();
                        this.shadeControlTimeouts[key] = timeout;
                    }
                    timeout.clear();
                    if (op.event === 'press') {
                        yield this.socket.send(`#DEVICE,${config.integrationId},${buttonNumber},3`, new RegExp(`~DEVICE,${config.integrationId},${buttonNumber},3|~Error`, 'i'), tags);
                        if (config.actions === 'pressReleaseHold') {
                            timeout.set(() => __awaiter(this, void 0, void 0, function* () {
                                if (this.framework.phase === 'controlling') {
                                    yield this.socket.send(`#DEVICE,${config.integrationId},${buttonNumber},5`, new RegExp(`~DEVICE,${config.integrationId},${buttonNumber},5|~Error`, 'i'), tags);
                                }
                            }), this.holdTimeoutMs);
                        }
                    }
                    else {
                        yield this.socket.send(`#DEVICE,${config.integrationId},${buttonNumber},4`, new RegExp(`~DEVICE,${config.integrationId},${buttonNumber},4|~Error`, 'i'), tags);
                    }
                }
                else {
                    let action = 'stop';
                    let actions = (_l = config[op.type]) === null || _l === void 0 ? void 0 : _l.actions;
                    if (actions === undefined) {
                        this.log.error(`no actions defined for ${op.type} in config: ${JSON.stringify(config)}`);
                        actions = 'raiseLowerStop';
                    }
                    if (config.behavior === 'holdToMove') {
                        if (op.motion === 'open' || op.motion === 'increase') {
                            action = op.event === 'press' ? 'raise' : 'stop';
                        }
                        else if (op.motion === 'close' || op.motion === 'decrease') {
                            action = op.event === 'press' ? 'lower' : 'stop';
                        }
                    }
                    else if (config.behavior === 'pressToStart') {
                        if (op.event === 'release')
                            return op;
                        action =
                            op.motion === 'open' || op.motion === 'increase'
                                ? 'raise'
                                : op.motion === 'close' || op.motion === 'decrease'
                                    ? 'lower'
                                    : 'stop';
                    }
                    else {
                        this.log.error(`unsupported behavior: ${config.behavior}`);
                    }
                    yield this.sendRaiseLowerStop(config, actions, action, tags);
                }
                return op;
            }),
            getKey: config => {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3;
                if (!isLutronShadeControlConfig(config)) {
                    this.log.error(`invalid shade control config: ${JSON.stringify(config)}`);
                    return 'unknown';
                }
                let key = '';
                if (config.type === 'lutronButtonPress') {
                    key = `${config.type}.${config.integrationId}`;
                    key += `.${(_d = (_c = (_b = (_a = config.position) === null || _a === void 0 ? void 0 : _a.buttons) === null || _b === void 0 ? void 0 : _b.open) === null || _c === void 0 ? void 0 : _c.buttonNumber) !== null && _d !== void 0 ? _d : 0}`;
                    key += `.${(_h = (_g = (_f = (_e = config.position) === null || _e === void 0 ? void 0 : _e.buttons) === null || _f === void 0 ? void 0 : _f.close) === null || _g === void 0 ? void 0 : _g.buttonNumber) !== null && _h !== void 0 ? _h : 0}`;
                    key += `.${(_m = (_l = (_k = (_j = config.position) === null || _j === void 0 ? void 0 : _j.buttons) === null || _k === void 0 ? void 0 : _k.stop) === null || _l === void 0 ? void 0 : _l.buttonNumber) !== null && _m !== void 0 ? _m : 0}`;
                    key += `.${(_r = (_q = (_p = (_o = config.tilt) === null || _o === void 0 ? void 0 : _o.buttons) === null || _p === void 0 ? void 0 : _p.increase) === null || _q === void 0 ? void 0 : _q.buttonNumber) !== null && _r !== void 0 ? _r : 0}`;
                    key += `.${(_v = (_u = (_t = (_s = config.tilt) === null || _s === void 0 ? void 0 : _s.buttons) === null || _t === void 0 ? void 0 : _t.decrease) === null || _u === void 0 ? void 0 : _u.buttonNumber) !== null && _v !== void 0 ? _v : 0}`;
                    key += `.${(_z = (_y = (_x = (_w = config.tilt) === null || _w === void 0 ? void 0 : _w.buttons) === null || _x === void 0 ? void 0 : _x.stop) === null || _y === void 0 ? void 0 : _y.buttonNumber) !== null && _z !== void 0 ? _z : 0}`;
                }
                else {
                    key = `${config.type}.${config.integrationId}`;
                    key += `.${(_1 = (_0 = config.position) === null || _0 === void 0 ? void 0 : _0.actions) !== null && _1 !== void 0 ? _1 : 'none'}`;
                    key += `.${(_3 = (_2 = config.tilt) === null || _2 === void 0 ? void 0 : _2.actions) !== null && _3 !== void 0 ? _3 : 'none'}`;
                }
                this.shadeControlConfigs[key] = Object.assign({}, config);
                return key;
            },
            validate: (config) => __awaiter(this, void 0, void 0, function* () {
                if (!isLutronShadeControlConfig(config)) {
                    this.log.error(`invalid shade control config: ${JSON.stringify(config)}`);
                    return { integrationId: 'missing' };
                }
                return { integrationId: 'ok' };
            }),
        };
        this.shadeOperations = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const shadeOperations = {};
                for (const [key, config] of Object.entries(this.shadeControlConfigs)) {
                    const ops = [];
                    if (config.position !== undefined)
                        ops.push('position');
                    if (config.tilt !== undefined)
                        ops.push('tilt');
                    shadeOperations[key] = ops;
                }
                return shadeOperations;
            }),
        };
        this.shadePreset = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                return key === this.lastPresetKey;
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const config = this.shadePresetConfigs[key];
                if (config === undefined) {
                    this.log.error(`missing shade control config for key = ${key}`);
                }
                else if (config.type === 'lutronAreaScene') {
                    yield this.socket.send(`#AREA,${config.integrationId},6,${config.sceneNumber}`, new RegExp(`~AREA,${config.integrationId},6,${config.sceneNumber}|~Error`, 'i'), tags);
                }
                else if (config.type === 'lutronButtonPreset') {
                    yield this.socket.send(`#DEVICE,${config.integrationId},${config.buttonNumber},3`, new RegExp(`~DEVICE,${config.integrationId},${config.buttonNumber},3|~Error`, 'i'), tags);
                }
                else if (config.type === 'lutronButtonPress') {
                    // Send both press and release since some actions are triggered on the release.
                    yield this.socket.send(`#DEVICE,${config.integrationId},${config.buttonNumber},3`, new RegExp(`~DEVICE,${config.integrationId},${config.buttonNumber},3|~Error`, 'i'), tags);
                    yield this.socket.send(`#DEVICE,${config.integrationId},${config.buttonNumber},4`, new RegExp(`~DEVICE,${config.integrationId},${config.buttonNumber},4|~Error`, 'i'), tags);
                }
                else if (config.type === 'lutronSceneControllerScene') {
                    yield this.socket.send(`#DEVICE,${config.integrationId},141,7,${config.sceneNumber}`, new RegExp(`~DEVICE,${config.integrationId},141,7,${config.sceneNumber}|~Error`, 'i'), tags);
                }
                else if (config.type === 'lutronShadeGroupPreset') {
                    yield this.socket.send(`#SHADEGRP,${config.integrationId},6,${config.presetNumber}`, new RegExp(`~SHADEGRP,${config.integrationId},6,${config.presetNumber}|~Error`, 'i'), tags);
                }
                return value;
            }),
            getKey: config => {
                if (!isLutronShadePresetConfig(config)) {
                    this.log.error(`invalid shade preset config: ${JSON.stringify(config)}`);
                    return 'unknown';
                }
                const key = `${config.type}.${config.integrationId}`;
                this.shadePresetConfigs[key] = Object.assign({}, config);
                return key;
            },
            validate: (config) => __awaiter(this, void 0, void 0, function* () {
                if (!isLutronShadePresetConfig(config)) {
                    this.log.error(`invalid shade preset config: ${JSON.stringify(config)}`);
                    return { integrationId: 'missing' };
                }
                return { integrationId: 'ok' };
            }),
        };
        // polling
        this.polls = [
            {
                intervalMs: 3000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    // Send a dummy command to confirm socket activity.
                    if (this.equipmentType === EquipmentType.QsStandalone) {
                        yield this.socket.send('aveo', /~Error|unknown command/, ['poll']);
                    }
                    else {
                        yield this.socket.send('?DEVICE,aveo,101,9', /~Error|unknown command|~DEVICE,1,101,9,\d+/i, ['poll']);
                    }
                }),
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            this.lastPresetKey = undefined;
            this.lightingPresets = {};
            this.lightingZones = {};
            this.shadeControlConfigs = {};
            Object.values(this.shadeControlTimeouts).forEach(timeout => timeout.destroy());
            this.shadeControlTimeouts = {};
            this.shadePresetConfigs = {};
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            Object.values(this.shadeControlTimeouts).forEach(timeout => timeout.destroy());
            this.shadeControlTimeouts = {};
        });
    }
    onInitialize(credentials, config) {
        return __awaiter(this, void 0, void 0, function* () {
            let initializerSteps = this.qsStandaloneInitializerSteps;
            if (config.equipmentType === EquipmentType.QsStandalone) {
                try {
                    yield this.socket.waitFor(/login:/i, {
                        unterminatedRegExp: /login:/i,
                        name: 'login',
                    });
                    initializerSteps = this.qsStandalonePassphraseInitializerSteps;
                }
                catch (error) {
                    initializerSteps = this.qsStandaloneInitializerSteps;
                }
            }
            else if (config.equipmentType === EquipmentType.Quantum) {
                initializerSteps = this.quantumInitializerSteps;
            }
            else if (config.equipmentType === EquipmentType.RadioRa2) {
                initializerSteps =
                    this.socket.port === 23
                        ? (initializerSteps = this.radioRa2TelnetInitializerSteps)
                        : (initializerSteps = this.radioRa2SerialInitializerSteps);
            }
            else {
                this.log.error(`invalid equipment type: ${config.equipmentType}`);
                return 'verifyFailed';
            }
            this.equipmentType = config.equipmentType;
            return yield CommandSocketInitializer.initialize(this.socket, initializerSteps, credentials);
        });
    }
    // other class methods
    sendRaiseLowerStop(config, actions, action, tags) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const actionIndex = { raise: 0, lower: 1, stop: 2 }[action];
            const actionNumber = ((_b = (_a = actionsMap[config.type]) === null || _a === void 0 ? void 0 : _a[actions]) !== null && _b !== void 0 ? _b : [2, 3, 4])[actionIndex];
            if (config.type === 'lutronAreaRaiseLower') {
                yield this.socket.send(`#AREA,${config.integrationId},${actionNumber}`, new RegExp(`~AREA,${config.integrationId},${actionNumber}|~Error`, 'i'), tags);
            }
            else if (config.type === 'lutronSceneControllerRaiseLower') {
                yield this.socket.send(`#DEVICE,${config.integrationId},141,${actionNumber}`, new RegExp(`~DEVICE,${config.integrationId},141,${actionNumber}|~Error`, 'i'), tags);
            }
            else if (config.type === 'lutronOutputRaiseLower') {
                yield this.socket.send(`#OUTPUT,${config.integrationId},${actionNumber}`, new RegExp(`~OUTPUT,${config.integrationId},${actionNumber}|~Error`, 'i'), tags);
            }
            else if (config.type === 'lutronShadeGroupRaiseLower') {
                yield this.socket.send(`#SHADEGRP,${config.integrationId},${actionNumber}`, new RegExp(`~SHADEGRP,${config.integrationId},${actionNumber}|~Error`, 'i'), tags);
            }
        });
    }
}
// static properties
Lutron.driverType = 'socketDriver';
Lutron.key = '-MDAF_dguZcRLLwbV5o5';
Lutron.socketOptions = (config) => {
    const sendTerminator = '\r\n';
    return [
        { sendTerminator, port: 23 },
        { sendTerminator, port: 4999 },
    ];
};
